import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Tooltip } from "@mui/material";
import { getWeightColor, formatWeight } from "../utils";
import { WeightMode } from "../DummyWeightInputTab/types";
import WeightCellContainer from "./WeightCell.styles";
import WeightHistoryChart from "../WeightHistoryChart/WeightHistoryChart";

const RATIONALE_MAX_LENGTH = 500;

const HOVER_DELAY_MS = 400;
const LEAVE_DELAY_MS = 120; // Grace period so mouse can travel into the popover

interface WeightCellProps {
    weight: number | null | undefined;
    portfolioWeight: number | null | undefined;
    editable?: boolean;
    isEditing?: boolean;
    isSaving?: boolean;
    editValue?: string;
    editMode?: WeightMode;
    editRationale?: string;
    rationale?: string | null;
    companyId?: string;
    companyName?: string;
    bbgTicker?: string;
    showHistory?: boolean;
    setEditValue?: (value: string) => void;
    setEditRationale?: (value: string) => void;
    onModeChange?: (mode: WeightMode) => void;
    onEdit?: () => void;
    onSave?: (companyId: string) => void;
    onCancel?: () => void;
}

const WeightCell = ({
    weight,
    portfolioWeight,
    editable = false,
    isEditing = false,
    isSaving = false,
    editValue = "",
    editMode = "custom",
    editRationale = "",
    rationale = null,
    companyId = "",
    companyName,
    bbgTicker,
    showHistory = false,
    setEditValue,
    setEditRationale,
    onModeChange,
    onEdit,
    onSave,
    onCancel,
}: WeightCellProps) => {
    const [showChart, setShowChart] = useState(false);
    const [popoverPos, setPopoverPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const cellRef = useRef<HTMLDivElement>(null);
    const enterTimerRef = useRef<NodeJS.Timeout | null>(null);
    const leaveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const colorClass =
        weight === undefined ? "" : getWeightColor(weight, portfolioWeight);

    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    useEffect(() => {
        return () => {
            if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
            if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        };
    }, []);

    const handleMouseEnter = () => {
        if (!showHistory || !bbgTicker || isEditing) return;
        if (leaveTimerRef.current) {
            clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
        if (!showChart) {
            enterTimerRef.current = setTimeout(() => {
                if (cellRef.current) {
                    const rect = cellRef.current.getBoundingClientRect();
                    const POPOVER_HEIGHT = 340;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const top =
                        spaceBelow >= POPOVER_HEIGHT
                            ? rect.bottom + window.scrollY + 8
                            : rect.top + window.scrollY - POPOVER_HEIGHT - 8;
                    setPopoverPos({ top, left: rect.left + window.scrollX });
                }
                setShowChart(true);
            }, HOVER_DELAY_MS);
        }
    };

    const handleMouseLeave = () => {
        if (enterTimerRef.current) {
            clearTimeout(enterTimerRef.current);
            enterTimerRef.current = null;
        }
        leaveTimerRef.current = setTimeout(() => {
            setShowChart(false);
        }, LEAVE_DELAY_MS);
    };

    if (isEditing && editable) {
        return (
            <WeightCellContainer>
                <div className="edit-container">
                    <div className="mode-selector">
                        <label className="mode-option">
                            <input
                                type="radio"
                                name={`weight-mode-${companyId}`}
                                checked={editMode === "live"}
                                onChange={() => onModeChange?.("live")}
                                onClick={stopPropagation}
                            />
                            <span>Live portfolio weight</span>
                        </label>
                        <label className="mode-option">
                            <input
                                type="radio"
                                name={`weight-mode-${companyId}`}
                                checked={editMode === "custom"}
                                onChange={() => onModeChange?.("custom")}
                                onClick={stopPropagation}
                            />
                            <span>Custom weight</span>
                        </label>
                    </div>
                    {editMode === "custom" ? (
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            className="weight-input"
                            value={editValue}
                            onChange={(e) => setEditValue?.(e.target.value)}
                            placeholder="0.0"
                            autoFocus
                            onClick={stopPropagation}
                        />
                    ) : (
                        <span className="live-weight-preview">
                            {formatWeight(portfolioWeight)}
                        </span>
                    )}
                    <div className="rationale-field">
                        <label
                            className="rationale-label"
                            htmlFor={`rationale-${companyId}`}
                        >
                            Rationale{" "}
                            <span className="rationale-optional">
                                (optional)
                            </span>
                        </label>
                        <textarea
                            id={`rationale-${companyId}`}
                            className="rationale-input"
                            value={editRationale}
                            onChange={(e) => setEditRationale?.(e.target.value)}
                            maxLength={RATIONALE_MAX_LENGTH}
                            onClick={stopPropagation}
                        />
                        <span className="rationale-counter">
                            {editRationale.length}/{RATIONALE_MAX_LENGTH}
                        </span>
                    </div>
                    <div className="action-buttons">
                        <button
                            className="action-btn save"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSave?.(companyId);
                            }}
                            disabled={isSaving}
                        >
                            Save
                        </button>
                        <button
                            className="action-btn cancel"
                            onClick={(e) => {
                                e.stopPropagation();
                                onCancel?.();
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </WeightCellContainer>
        );
    }

    const trimmedRationale = rationale?.trim() || "";
    const weightText = (
        <span className={colorClass}>{formatWeight(weight)}</span>
    );

    return (
        <WeightCellContainer>
            <div
                ref={cellRef}
                className="weight-display"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {trimmedRationale ? (
                    <Tooltip title={trimmedRationale} placement="top" arrow>
                        {weightText}
                    </Tooltip>
                ) : (
                    weightText
                )}
                {editable && (
                    <button
                        className="action-btn edit"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.();
                        }}
                        disabled={isEditing}
                    >
                        Edit
                    </button>
                )}
                {showChart &&
                    bbgTicker &&
                    companyName &&
                    companyId &&
                    popoverPos &&
                    ReactDOM.createPortal(
                        <div
                            className="chart-popover"
                            style={{
                                position: "absolute",
                                top: popoverPos.top,
                                left: popoverPos.left,
                                zIndex: 2000,
                            }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <WeightHistoryChart
                                bbgTicker={bbgTicker}
                                companyId={companyId}
                                companyName={companyName}
                            />
                        </div>,
                        document.body
                    )}
            </div>
        </WeightCellContainer>
    );
};

export default WeightCell;
